import {
    ChangeDetectionStrategy, Component, ContentChild, Input
} from "@angular/core";
import {
    MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle
} from "@angular/material/expansion";
import { IndicatorComponent } from "../../../application/shared/indicator/indicator.component";
import { SubformComponent } from "../subform/subform.component";
import { FormSectionHeaderDescriptionDirective } from "../../directives/form-section-header-description/form-section-header-description.directive";

/**
 * Container component that wraps a subform and form list in a Material Expansion Panel with a header.
 * Provides collapsible visual grouping and styling for related form sections.
 *
 * @example
 * <mgd-expandable-form-section [count]="subformCount">
 *   <span mgdFormSectionIcon><mat-icon>distance</mat-icon></span>
 *   <span mgdFormSectionHeader>Subform Component</span>
 *   <mgd-subform #mgdSubform="mgdSubform" [formGroup]="subformGroup" (submitSubform)="onSubmitSubform($event)">
 *     <mat-form-field>
 *       <mat-label>Form Field 1</mat-label>
 *       <input matInput formControlName="subformField1" />
 *     </mat-form-field>
 *   </mgd-subform>
 *   <mgd-form-list formControlName="list" [subform]="mgdSubform">
 *     <mat-list-item mgdFormListItem="item" *mgdFormListData="let data; let i = index">
 *       <span>{{i}}: {{data.subformField1}}</span>
 *     </mat-list-item>
 *   </mgd-form-list>
 * </mgd-expandable-form-section>
 */
@Component({
    selector: "mgd-expandable-form-section",
    imports: [
        IndicatorComponent,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle
    ],
    templateUrl: "./expandable-form-section.component.html",
    styleUrl: "./expandable-form-section.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpandableFormSectionComponent {

    /**
     * When true, the expansion panel is open on initial render.
     * Defaults to false (collapsed).
     */
    @Input() expanded: boolean = false;

    /**
     * Optional item count shown as a pill at the trailing end of the header title row.
     * Nothing is rendered when falsy (`0` / `undefined`).
     */
    @Input() count?: number;

    /**
     * SubformComponent within the ExpandableFormSectionComponent Content Children
     */
    @ContentChild(SubformComponent) subform?: SubformComponent;

    /**
     * FormSectionHeaderDescriptionDirective within the ExpandableFormSectionComponent Content Children.
     * Presence drives whether the header's description wrapper is rendered at all.
     */
    @ContentChild(FormSectionHeaderDescriptionDirective) headerDescription?: FormSectionHeaderDescriptionDirective;

}
